import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BracketMatch from './BracketMatch';
import api from '../api/client';

function TeamModal({ team, onClose }) {
  if (!team) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg text-ocean-900">{team.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        <div className="space-y-2">
          {(team.members || []).filter((m) => m.status === 'confirmed').map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
              <div>
                <Link to={`/users/${m.user_id}`} className="font-medium text-slate-800 hover:text-ocean-600 hover:underline">
                  {m.user?.nickname || m.user?.username || '–'}
                </Link>
                {m.role === 'creator' && (
                  <span className="ml-2 text-[10px] bg-ocean-100 text-ocean-700 px-1.5 py-0.5 rounded font-semibold uppercase">Captain</span>
                )}
              </div>
              {m.seat_label ? (
                <span className="text-xs font-bold bg-sand-100 text-sand-700 px-2 py-1 rounded border border-sand-200">
                  Platz {m.seat_label}
                </span>
              ) : (
                <span className="text-xs text-slate-300">Kein Platz</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Single match column with vertical spacing to align with bracket connections
function BracketColumn({ matches, roundLabel, note, onTeamClick, onMatchClick, isAdmin, onNoteSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await onNoteSave(draft.trim()); setEditing(false); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col items-center min-w-[184px]">
      <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 text-center">{roundLabel}</div>

      {/* Note display / edit */}
      <div className="mb-2 w-full px-1">
        {editing ? (
          <div className="flex gap-1 items-center">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
              placeholder="Map / Notiz..."
              className="flex-1 text-xs border border-ocean-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-ocean-400 min-w-0"
            />
            <button
              onClick={save} disabled={saving}
              className="text-xs bg-ocean-500 text-white px-2 py-1 rounded hover:bg-ocean-600 disabled:opacity-50 shrink-0">
              {saving ? '…' : '✓'}
            </button>
            <button
              onClick={() => { setDraft(note || ''); setEditing(false); }}
              className="text-xs text-slate-400 hover:text-slate-600 px-1 py-1 shrink-0">
              ✕
            </button>
          </div>
        ) : (
          <div
            className={`flex items-center justify-center gap-1 min-h-[22px] ${isAdmin ? 'cursor-pointer group' : ''}`}
            onClick={() => isAdmin && setEditing(true)}
            title={isAdmin ? 'Klicken zum Bearbeiten' : undefined}
          >
            {note ? (
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded truncate max-w-[170px]" title={note}>
                🗺 {note}
              </span>
            ) : isAdmin ? (
              <span className="text-xs text-slate-300 group-hover:text-ocean-400 transition-colors">+ Notiz</span>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {matches.map((m) => (
          <BracketMatch key={m.id} match={m} onTeamClick={onTeamClick} onMatchClick={onMatchClick} />
        ))}
      </div>
    </div>
  );
}

function SingleEliminationBracket({ matches, teams, noteMap, onTeamClick, onMatchClick, isAdmin, tournamentId, onNoteUpdated }) {
  const saveNote = (roundNumber, bracketSide) => async (note) => {
    await api.put(`/api/admin/tournaments/${tournamentId}/round-notes`,
      { round_number: roundNumber, bracket_side: bracketSide, note });
    if (onNoteUpdated) onNoteUpdated();
  };
  const roundNums = [...new Set(
    matches.filter((m) => m.bracket_side === 'winners' || m.bracket_side === 'third_place')
      .map((m) => m.round)
  )].sort((a, b) => a - b);

  const mainRounds = roundNums.filter((r) => {
    const roundMatches = matches.filter((m) => m.round === r && m.bracket_side === 'winners');
    return roundMatches.length > 0;
  });

  const finalRound = Math.max(...mainRounds);
  const thirdPlaceMatches = matches.filter((m) => m.bracket_side === 'third_place');
  const thirdNote = noteMap[`${finalRound}_third_place`] || '';

  return (
    <div className="flex flex-col gap-6">
      {/* Main bracket */}
      <div className="rounded-xl border border-ocean-200 overflow-hidden">
        <div className="bg-ocean-600 px-4 py-2.5">
          <span className="text-white font-bold text-sm">Turnierbaum</span>
        </div>
        <div className="p-4 overflow-x-auto">
          <div className="flex items-start gap-6 pb-2">
            {mainRounds.map((r, idx) => {
              const roundMatches = matches.filter((m) => m.round === r && m.bracket_side === 'winners');
              const label = r === finalRound ? 'Finale' :
                r === finalRound - 1 ? 'Halbfinale' :
                r === finalRound - 2 ? 'Viertelfinale' :
                `Runde ${r}`;
              return (
                <React.Fragment key={r}>
                  <BracketColumn
                    matches={roundMatches}
                    roundLabel={label}
                    note={noteMap[`${r}_winners`] || ''}
                    onTeamClick={onTeamClick}
                    onMatchClick={onMatchClick}
                    isAdmin={isAdmin}
                    onNoteSave={saveNote(r, 'winners')}
                  />
                  {idx < mainRounds.length - 1 && (
                    <div className="flex items-center self-stretch">
                      <div className="w-6 border-t-2 border-slate-200" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3rd place match */}
      {thirdPlaceMatches.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-500 px-4 py-2.5">
            <span className="text-white font-bold text-sm">Spiel um Platz 3</span>
          </div>
          <div className="p-4">
            <BracketColumn
              matches={thirdPlaceMatches}
              roundLabel=""
              note={thirdNote}
              onTeamClick={onTeamClick}
              onMatchClick={onMatchClick}
              isAdmin={isAdmin}
              onNoteSave={saveNote(finalRound, 'third_place')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DoubleEliminationBracket({ matches, teams, noteMap, onTeamClick, onMatchClick, isAdmin, tournamentId, onNoteUpdated }) {
  const saveNote = (roundNumber, bracketSide) => async (note) => {
    await api.put(`/api/admin/tournaments/${tournamentId}/round-notes`,
      { round_number: roundNumber, bracket_side: bracketSide, note });
    if (onNoteUpdated) onNoteUpdated();
  };
  const wbMatches = matches.filter((m) => m.bracket_side === 'winners');
  const lbMatches = matches.filter((m) => m.bracket_side === 'losers');
  const gfMatches = matches.filter((m) => m.bracket_side === 'grand_final' || m.bracket_side === 'grand_final_reset');

  const wbRounds = [...new Set(wbMatches.map((m) => m.round))].sort((a, b) => a - b);
  const lbRounds = [...new Set(lbMatches.map((m) => m.round))].sort((a, b) => a - b);

  const wbFinalRound = Math.max(...wbRounds, 0);

  const getRoundLabel = (r, totalRounds, prefix = '') => {
    const fromEnd = totalRounds - r;
    if (fromEnd === 0) return `${prefix}Finale`;
    if (fromEnd === 1) return `${prefix}Halbfinale`;
    return `${prefix}R${r}`;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Winners Bracket */}
      <div className="rounded-xl border border-ocean-200 overflow-hidden">
        <div className="bg-ocean-600 px-4 py-2.5 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-ocean-300 inline-block" />
          <span className="text-white font-bold text-sm">Winners Bracket</span>
        </div>
        <div className="p-4 overflow-x-auto">
          <div className="flex items-start gap-6 pb-2">
            {wbRounds.map((r, idx) => (
              <React.Fragment key={r}>
                <BracketColumn
                  matches={wbMatches.filter((m) => m.round === r)}
                  roundLabel={getRoundLabel(r, wbFinalRound)}
                  note={noteMap[`${r}_winners`] || ''}
                  onTeamClick={onTeamClick}
                  onMatchClick={onMatchClick}
                  isAdmin={isAdmin}
                  onNoteSave={saveNote(r, 'winners')}
                />
                {idx < wbRounds.length - 1 && (
                  <div className="flex items-center self-stretch">
                    <div className="w-6 border-t-2 border-ocean-200" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Losers Bracket */}
      {lbRounds.length > 0 && (
        <div className="rounded-xl border border-amber-200 overflow-hidden">
          <div className="bg-amber-500 px-4 py-2.5 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-200 inline-block" />
            <span className="text-white font-bold text-sm">Losers Bracket</span>
          </div>
          <div className="p-4 overflow-x-auto">
            <div className="flex items-start gap-6 pb-2">
              {lbRounds.map((r, idx) => (
                <React.Fragment key={r}>
                  <BracketColumn
                    matches={lbMatches.filter((m) => m.round === r)}
                    roundLabel={`LB R${r}`}
                    note={noteMap[`${r}_losers`] || ''}
                    onTeamClick={onTeamClick}
                    onMatchClick={onMatchClick}
                    isAdmin={isAdmin}
                    onNoteSave={saveNote(r, 'losers')}
                  />
                  {idx < lbRounds.length - 1 && (
                    <div className="flex items-center self-stretch">
                      <div className="w-6 border-t-2 border-amber-200" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grand Final */}
      {gfMatches.length > 0 && (
        <div className="rounded-xl border border-sand-300 overflow-hidden">
          <div className="bg-sand-500 px-4 py-2.5">
            <span className="text-white font-bold text-sm">Grand Final</span>
          </div>
          <div className="p-4">
            <BracketColumn
              matches={gfMatches}
              roundLabel=""
              note={noteMap['1_grand_final'] || ''}
              onTeamClick={onTeamClick}
              onMatchClick={onMatchClick}
              isAdmin={isAdmin}
              onNoteSave={saveNote(1, 'grand_final')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function BracketView({ tournament, matches, teams, roundNotes, onMatchClick, isAdmin, tournamentId, onNoteUpdated, hideLegend }) {
  const [selectedTeam, setSelectedTeam] = useState(null);

  const teamsById = (teams || []).reduce((acc, t) => { acc[t.id] = t; return acc; }, {});

  // Enrich matches with team objects
  const enrichedMatches = (matches || []).map((m) => ({
    ...m,
    team1: m.team1_id ? (teamsById[m.team1_id] || m.team1) : null,
    team2: m.team2_id ? (teamsById[m.team2_id] || m.team2) : null,
  }));

  // Build note lookup: "round_bracketside" → note text
  const noteMap = {};
  (roundNotes || []).forEach((n) => { noteMap[`${n.round_number}_${n.bracket_side}`] = n.note; });

  const handleTeamClick = (team) => {
    const fullTeam = teamsById[team.id] || team;
    setSelectedTeam(fullTeam);
  };

  if (!matches || matches.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <div className="text-4xl mb-3">🏆</div>
        <p className="font-medium">Bracket noch nicht generiert</p>
        <p className="text-sm mt-1">Der Admin generiert den Bracket sobald alle Teams voll sind.</p>
      </div>
    );
  }

  const isDouble = tournament?.format === 'double_elimination';

  return (
    <>
      <div className="overflow-x-auto">
        {isDouble ? (
          <DoubleEliminationBracket
            matches={enrichedMatches}
            teams={teams}
            noteMap={noteMap}
            onTeamClick={handleTeamClick}
            onMatchClick={onMatchClick}
            isAdmin={isAdmin}
            tournamentId={tournamentId}
            onNoteUpdated={onNoteUpdated}
          />
        ) : (
          <SingleEliminationBracket
            matches={enrichedMatches}
            teams={teams}
            noteMap={noteMap}
            onTeamClick={handleTeamClick}
            onMatchClick={onMatchClick}
            isAdmin={isAdmin}
            tournamentId={tournamentId}
            onNoteUpdated={onNoteUpdated}
          />
        )}
      </div>

      {/* Legend */}
      {!hideLegend && (
        <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-slate-200 bg-white inline-block" />
            Offen
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-amber-300 bg-amber-50 inline-block" />
            Bestätigung ausstehend
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-emerald-300 bg-white inline-block" />
            Abgeschlossen
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-slate-200 bg-slate-50 inline-block" />
            BYE
          </span>
          <span className="text-slate-400">Klick auf Teamname = Spielerliste mit Sitzplätzen</span>
        </div>
      )}

      <TeamModal team={selectedTeam} onClose={() => setSelectedTeam(null)} />
    </>
  );
}
